import * as React from 'react';

import { cn } from '../../utils/cn';
import { QUESTIONNAIRE_COLOUR } from './questionnaire-tokens';

export interface QuestionnaireFooterProps {
    onBack?: () => void;
    onContinue?: () => void;
    backLabel?: string;
    continueLabel?: string;
    /** Hidden on the opening step, where there is nowhere to go back to. */
    showBack?: boolean;
    /** Continue is disabled until the step's answer satisfies its rule. */
    canContinue?: boolean;
    /** Suppresses both actions while a submit is in flight. */
    busy?: boolean;
    /**
     * Drops Back and Continue but keeps the rule and the legal row, for screens
     * that carry their own actions — the account wall and the outcomes.
     */
    hideActions?: boolean;
    /** Legal row under the rule. Defaults to copyright, Terms, Privacy Policy. */
    legal?: React.ReactNode;
    className?: string;
}

const PILL_BASE =
    'inline-flex h-10 items-center justify-center rounded-full px-4.5 font-sans text-3.75 leading-[1.07] tracking-[-0.03em] transition-colors disabled:cursor-not-allowed';

export const QuestionnaireFooter = ({
    onBack,
    onContinue,
    backLabel = 'Back',
    continueLabel = 'Continue',
    showBack = true,
    canContinue = true,
    busy = false,
    hideActions = false,
    legal,
    className,
}: QuestionnaireFooterProps) => (
    <div className={cn('flex flex-col', className)}>
        {hideActions ? null : (
            <div className="flex items-center justify-between gap-3">
                {showBack ? (
                    <button
                        className={cn(
                            PILL_BASE,
                            'border border-solid disabled:opacity-40',
                            QUESTIONNAIRE_COLOUR.actionOutline,
                            QUESTIONNAIRE_COLOUR.actionOutlineInk,
                            'hover:bg-[#f6f6f6]',
                        )}
                        disabled={busy}
                        onClick={onBack}
                        type="button"
                    >
                        {backLabel}
                    </button>
                ) : (
                    // Holds the Continue pill on the right when Back is absent.
                    <span aria-hidden="true" />
                )}

                <button
                    className={cn(
                        PILL_BASE,
                        'flex-1 text-white shadow-[0_2.167px_7.583px_rgba(0,0,0,0.11)] disabled:opacity-40 md:min-w-38.5 md:flex-none',
                        QUESTIONNAIRE_COLOUR.actionPrimary,
                        'hover:bg-[#181818]',
                    )}
                    disabled={busy || !canContinue}
                    onClick={onContinue}
                    type="button"
                >
                    {continueLabel}
                </button>
            </div>
        )}

        <hr
            className={cn(
                'w-full border-t',
                hideActions ? 'mt-0' : 'mt-0.75',
                QUESTIONNAIRE_COLOUR.hairline,
            )}
        />

        <div
            className={cn(
                'mt-0.5 flex flex-wrap items-center gap-x-6 gap-y-1 font-sans text-3 leading-[1.3] tracking-[-0.02em] md:gap-x-7',
                QUESTIONNAIRE_COLOUR.inkMuted,
            )}
        >
            {legal ?? (
                <>
                    <span>© ArchiPro {new Date().getFullYear()}</span>
                    <span className="w-3"></span>
                    <a className="hover:underline" href="/terms-of-use">
                        Terms
                    </a>
                    <a className="hover:underline" href="/privacy-policy">
                        Privacy Policy
                    </a>
                </>
            )}
        </div>
    </div>
);
